<template>

  <div class="vflex">

    <div class="vflex-row-0 vflex-header p-3">
      <div class="page-title">
        <div class="title">
          <h4>{{ $t('Calendar settings') }}</h4>
        </div>
      </div>
    </div>

    <div class="vflex-row-1 p-3">
      <creating
        v-if="creating"
        height="100%" />

      <div
        v-else
        :class="{'box-loading': loading}">

        <!--conexão do membro-->
        <ProfileCalendarConnection
          v-if="currentProvider"
          :provider="currentProvider"
          :connection="connection"
          @updated="loadData()"
          @removed="loadData()" />

        <!--provedores disponíveis (só enquanto não existe conexão)-->
        <div
          v-else
          class="intro">

          <!--chamada da integração: o que o cliente ganha ao conectar-->
          <h4 class="mb-2">Todas as suas atividades em um único lugar</h4>
          <p class="lead-text">
            Conecte a sua agenda e as atividades criadas aqui viram compromissos
            reais no seu calendário, com os seus horários ocupados sempre
            respeitados na hora de marcar algo novo.
          </p>

          <ul class="benefits">
            <li>
              <i class="fas fa-calendar-plus" />
              <span>Cada atividade agendada aparece automaticamente na sua agenda</span>
            </li>
            <li>
              <i class="fas fa-sync-alt" />
              <span>Mudou o horário aqui? O evento é atualizado na agenda também</span>
            </li>
          </ul>

          <!--cards de conexão: só o provedor e o botão-->
          <div
            class="card provider-card mb-2"
            v-for="provider in providers"
            :key="provider.type">
            <div class="card-body d-flex align-items-center">
              <i
                class="provider-icon"
                :class="provider.icon" />
              <div class="mr-auto provider-label">{{ provider.label }}</div>
              <a
                class="btn btn-primary"
                :href="authUrl(provider)">{{ $t('Connect') }}</a>
            </div>
          </div>

          <!--
          aviso de uso de dados do fabricante. exigido por Google/Microsoft,
          então sai dos cards mas continua na tela (renderizado com v-html)
          -->
          <p
            class="subtext policy mt-3 mb-0"
            v-for="provider in providers"
            :key="provider.type + '-policy'"><i v-html="provider.policy"></i></p>
        </div>
      </div>
    </div>

  </div>

</template>

<script>
import config from '../../config'
import ProfileCalendarConnection from './ProfileCalendarConnection'

export default {
  name: 'ProfileCalendarSettings',
  components: {
    ProfileCalendarConnection
  },
  data () {
    return {
      /*
      providers
      provedores aceitos pela integração de agenda. os dois usam exatamente as
      mesmas rotas, então só mudam o tipo, o rótulo e o aviso de uso de dados do
      fabricante (renderizado com v-html).
      */
      providers: [{
        type: 'googlecalendar',
        label: 'Google Calendar',
        icon: 'fab fa-google',
        policy: 'This app use and transfer of information received from Google APIs to any other app will adhere to <a target="_blank" href="https://developers.google.com/terms/api-services-user-data-policy#additional_requirements_for_specific_api_scopes">Google API Services User Data Policy</a>, including the Limited Use requirements.'
      }, {
        type: 'microsoftoutlook',
        label: 'Microsoft Outlook',
        icon: 'fab fa-microsoft',
        policy: 'This app use and transfer of information received from Microsoft APIs to any other app will adhere to <a target="_blank" href="https://docs.microsoft.com/en-us/graph/use-the-api">Microsoft API Services User Data Policy</a>, including the Limited Use requirements.'
      }],
      connection: null,
      creating: true,
      loading: true
    }
  },
  metaInfo () {
    return {
      title: this.$t('Calendar settings')
    }
  },
  computed: {
    accountId () {
      return this.$store.getters.getAccount._id
    },
    // GET|POST /profile/connections/calendar
    url () {
      return '/accounts/' + this.accountId + '/profile/connections/calendar'
    },
    // o provedor (googlecalendar / microsoftoutlook) vem no connection_config
    currentProvider () {
      if (!this.connection) return null
      const type = (this.connection.connection_config || {}).type
      return this.providers.find(p => p.type === type)
    },
    // a sincronização da agenda é acompanhada pelo metadata.sync_status
    syncing () {
      if (!this.connection) return false
      return (this.connection.metadata || {}).sync_status === 'syncing'
    }
  },
  created () {
    const query = this.$route.query
    const {
      id,
      nonce,
      sig,
      state,
      type
    } = query

    if (id && nonce && sig && type) {
      this.connect({ id, nonce, sig, state, type })
      this.$router.push({ name: this.$route.name, query: {} }) // remove params
    } else {
      this.loadData()
    }
  },
  methods: {
    /*
    authUrl
    url de autorização do provedor (unified.to). o retorno cai de novo nesta
    tela, com os dados da conexão na query (id, nonce, sig, state, type)
    */
    authUrl (provider) {
      const redirect = `${config.app_url}#/config/calendar-settings`
      const query = {
        success_redirect: redirect,
        failure_redirect: redirect,
        redirect: 'true',
        env: config.unified.env,
        scopes: [
          'calendar_calendar_write',
          'calendar_event_write',
          'calendar_busy_read'
        ].join(',')
      }
      return `${config.unified.api_url}/unified/integration/auth/${config.unified.workspace_id}/${provider.type}?` + Object.keys(query).map(key => `${key}=${encodeURIComponent(query[key])}`).join('&')
    },
    /*
    connect
    POST /profile/connections/calendar
    */
    async connect (params) {
      try {
        await this.axios.post(this.url, params)
      } catch (err) {
        console.log(err)
      }
      await this.loadData()
    },
    /*
    loadData
    GET /profile/connections (lista todas as conexões do membro)
    */
    async loadData () {
      this.loading = true
      try {
        const response = await this.axios.get('/accounts/' + this.accountId + '/profile/connections')
        this.connection = (response.data || []).find(item => item.integration === 'calendar') || null

        // polling if syncing
        if (this.syncing) {
          this.pooling()
        }
      } catch (err) {
        console.log(err)
        this.connection = null
      } finally {
        this.creating = this.loading = false
      }
    },
    /*
    pooling
    GET /profile/connections/calendar/:connection_id
    */
    async pooling () {
      try {
        const response = await this.axios.get(this.url + '/' + this.connection._id)
        this.connection = response.data

        // polling if syncing
        if (this.syncing) {
          await new Promise(resolve => setTimeout(resolve, 3000)) // wait 3 seconds
          await this.pooling()
        }
      } catch (err) {
        console.log(err)
      }
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.intro{
  max-width: 640px;
}

.lead-text{
  font-size: 1.05em;
  line-height: 1.5;
  opacity: 0.85;
}

.benefits{
  list-style: none;
  padding: 0;
  margin: 0 0 2em;
}
  .benefits li{
    display: flex;
    align-items: flex-start;
    margin-bottom: 0.6em;
  }
  .benefits i{
    flex: 0 0 1.5em;
    margin-top: 0.2em;
    opacity: 0.6;
  }

.provider-card .card-body{
  padding: 1em 1.25em;
}
.provider-icon{
  font-size: 1.4em;
  width: 1.6em;
  opacity: 0.7;
}
.provider-label{
  font-weight: 600;
}

.policy{
  font-size: 0.75em;
  line-height: 1.4;
  opacity: 0.7;
}
</style>



// WEBPACK FOOTER //
// src/components/ProfileCalendarSettings/ProfileCalendarSettings.vue